import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiDownload, FiTrash2, FiX, FiRefreshCw, FiFilter } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

function TableManagement() {
  const [tables, setTables] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ tableNumber: '', name: '' });
  const [resetting, setResetting] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadTables();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API_URL}/tables`);
      // Sort tables numerically by tableNumber
      const sortedTables = (response.data.data || []).sort((a, b) => {
        const numA = parseInt(a.tableNumber, 10) || 0;
        const numB = parseInt(b.tableNumber, 10) || 0;
        return numA - numB;
      });
      setTables(sortedTables);
    } catch (error) {
      toast.error('Không thể tải danh sách bàn');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/tables`, formData);
      toast.success('Thêm bàn thành công');
      loadTables();
      setShowModal(false);
      setFormData({ tableNumber: '', name: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể thêm bàn');
    }
  };

  const deleteTable = async (id) => {
    if (window.confirm('Xóa bàn này?')) {
      try {
        await axios.delete(`${API_URL}/tables/${id}`);
        toast.success('Đã xóa bàn');
        loadTables();
      } catch (error) {
        toast.error('Không thể xóa bàn');
      }
    }
  };

  const resetTable = async (table) => {
    if (window.confirm(`Reset ${table.name || 'Bàn ' + table.tableNumber}?\n\nKhách đang sử dụng bàn này sẽ phải quét mã QR lại.`)) {
      setResetting(table._id);
      try {
        const response = await axios.post(`${API_URL}/tables/${table._id}/reset`);
        toast.success(response.data.message || 'Đã reset bàn');
        loadTables();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Không thể reset bàn');
      } finally {
        setResetting(null);
      }
    }
  };

  const downloadQR = (table) => {
    const link = document.createElement('a');
    link.href = `${API_URL.replace('/api', '')}${table.qrCode}`;
    link.download = `ban-${table.tableNumber}-qr.png`;
    link.click();
  };

  const getStatusColor = (status) => {
    return status === 'available' ? 'bg-green-100 text-green-800' : 
           status === 'occupied' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (status) => {
    return status === 'available' ? 'Trống' : 
           status === 'occupied' ? 'Đang dùng' : 'Đặt trước';
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "space-y-6"}>
      {/* Header */}
      <div className={isMobile ? "bg-white px-4 py-3 flex items-center justify-between shadow-sm" : "flex justify-between items-center"}>
        <h1 className={isMobile ? "text-lg font-bold text-gray-800" : "text-3xl font-bold"}>Quản lý bàn</h1>
        <div className="flex items-center space-x-2">
          {isMobile && (
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border ${showFilters || statusFilter !== 'all' ? 'bg-green-50 border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}`}
            >
              <FiFilter size={18} />
            </button>
          )}
          <button 
            onClick={() => setShowModal(true)} 
            className={isMobile 
              ? "w-9 h-9 bg-green-500 text-white rounded-full flex items-center justify-center"
              : "flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            }
          >
            <FiPlus size={isMobile ? 20 : 16} />
            {!isMobile && <span>Thêm bàn</span>}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {isMobile ? (
        // Mobile: Collapsible filter
        showFilters && (
          <div className="bg-white px-4 py-3 border-b flex overflow-x-auto space-x-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${
                statusFilter === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Tất cả
            </button>
            <button
              onClick={() => setStatusFilter('available')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${
                statusFilter === 'available' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Trống
            </button>
            <button
              onClick={() => setStatusFilter('occupied')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${
                statusFilter === 'occupied' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Đang dùng
            </button>
          </div>
        )
      ) : (
        // Desktop: Full filter bar
        <div className="bg-white p-4 rounded-lg shadow flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <FiFilter className="text-gray-500" />
            <span className="text-gray-600 font-medium">Trạng thái:</span>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 text-sm font-medium transition ${
                  statusFilter === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Tất cả
              </button>
              <button
                onClick={() => setStatusFilter('available')}
                className={`px-4 py-2 text-sm font-medium transition border-l ${
                  statusFilter === 'available' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Trống
              </button>
              <button
                onClick={() => setStatusFilter('occupied')}
                className={`px-4 py-2 text-sm font-medium transition border-l ${
                  statusFilter === 'occupied' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Đang dùng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tables Grid */}
      <div className={isMobile ? "p-3" : ""}>
        <div className={isMobile 
          ? "grid grid-cols-2 gap-2" 
          : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4"
        }>
          {tables
            .filter(table => statusFilter === 'all' || table.status === statusFilter)
            .map(table => (
            <div key={table._id} className={isMobile ? "bg-white rounded-lg shadow p-2" : "bg-white rounded-lg shadow-md p-4"}>
              <div className="flex justify-between items-start mb-1">
                <div className="min-w-0 flex-1">
                  <h3 className={isMobile ? "font-bold text-sm truncate" : "font-bold text-lg"}>Bàn {table.tableNumber}</h3>
                  <p className="text-xs text-gray-600 truncate">{table.name}</p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ml-1 ${getStatusColor(table.status)}`}>
                  {isMobile ? (table.status === 'available' ? '✓' : '●') : getStatusText(table.status)}
                </span>
              </div>
              
              {table.qrCode && (
                <div className={isMobile ? "mb-2 bg-gray-50 p-1 rounded" : "mb-3 bg-gray-50 p-2 rounded-lg"}>
                  <img src={`${API_URL.replace('/api', '')}${table.qrCode}`} alt="QR Code" className="w-full" />
                </div>
              )}
              
              <div className={isMobile ? "flex space-x-1" : "flex flex-col space-y-1"}>
                {isMobile ? (
                  // Mobile: Icon buttons only
                  <>
                    <button onClick={() => downloadQR(table)} className="flex-1 bg-blue-600 text-white py-1.5 rounded text-xs hover:bg-blue-700 flex items-center justify-center">
                      <FiDownload size={14} />
                    </button>
                    <button 
                      onClick={() => resetTable(table)} 
                      disabled={resetting === table._id}
                      className="flex-1 bg-orange-500 text-white py-1.5 rounded text-xs hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      {resetting === table._id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : (
                        <FiRefreshCw size={14} />
                      )}
                    </button>
                    <button onClick={() => deleteTable(table._id)} className="flex-1 bg-red-600 text-white py-1.5 rounded text-xs hover:bg-red-700 flex items-center justify-center">
                      <FiTrash2 size={14} />
                    </button>
                  </>
                ) : (
                  // Desktop: Full buttons
                  <>
                    <div className="flex space-x-1">
                      <button onClick={() => downloadQR(table)} className="flex-1 bg-blue-600 text-white py-1.5 rounded text-sm hover:bg-blue-700 flex items-center justify-center space-x-1">
                        <FiDownload size={14} /><span>QR</span>
                      </button>
                      <button onClick={() => deleteTable(table._id)} className="flex-1 bg-red-600 text-white py-1.5 rounded text-sm hover:bg-red-700 flex items-center justify-center space-x-1">
                        <FiTrash2 size={14} /><span>Xóa</span>
                      </button>
                    </div>
                    <button 
                      onClick={() => resetTable(table)} 
                      disabled={resetting === table._id}
                      className="w-full bg-orange-500 text-white py-1.5 rounded text-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center space-x-1"
                    >
                      {resetting === table._id ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          <span>Đang reset...</span>
                        </>
                      ) : (
                        <>
                          <FiRefreshCw size={14} /><span>Reset bàn</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Thêm bàn mới</h2>
              <button onClick={() => setShowModal(false)}><FiX size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2">Số bàn *</label>
                <input type="text" value={formData.tableNumber} onChange={(e) => setFormData({...formData, tableNumber: e.target.value})} className="w-full border rounded-lg px-4 py-2" placeholder="VD: 1, 2, A1..." required />
              </div>
              <div>
                <label className="block font-semibold mb-2">Tên bàn *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border rounded-lg px-4 py-2" placeholder="VD: Bàn VIP, Bàn ngoài trời..." required />
              </div>
              <button type="submit" className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700">
                Thêm bàn
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TableManagement;
